import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  BookOpen, Plus, Edit, Trash2, Search, FolderOpen, FileText,
  Eye, Clock, User, Tag, ChevronRight, ArrowLeft, History, Save
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import api from '@/lib/api';

const AdminKnowledgeBase = () => {
  // State
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [view, setView] = useState('list'); // list, article, edit
  
  // Dialogs
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Form data
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    icon: 'folder',
    order: 0
  });
  
  const [articleForm, setArticleForm] = useState({
    title: '',
    content: '',
    category_id: '',
    excerpt: '',
    tags: [],
    is_published: true
  });
  
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [catRes, artRes] = await Promise.all([
        api.get('/admin/knowledge-base/categories'),
        api.get('/admin/knowledge-base/articles?published_only=false')
      ]);
      setCategories(catRes.data || []);
      setArticles(artRes.data || []);
    } catch (error) {
      toast.error('Failed to load knowledge base');
    } finally {
      setLoading(false);
    }
  };

  // Category handlers
  const openCategoryDialog = (category = null) => {
    if (category) {
      setCategoryForm({
        name: category.name,
        description: category.description || '',
        icon: category.icon || 'folder',
        order: category.order || 0
      });
      setSelectedCategory(category);
    } else {
      setCategoryForm({ name: '', description: '', icon: 'folder', order: 0 });
      setSelectedCategory(null);
    }
    setIsCategoryDialogOpen(true);
  };

  const saveCategory = async () => {
    if (!categoryForm.name) {
      toast.error('Category name is required');
      return;
    }
    
    try {
      if (selectedCategory) {
        await api.put(`/admin/knowledge-base/categories/${selectedCategory.id}`, categoryForm);
        toast.success('Category updated');
      } else {
        await api.post('/admin/knowledge-base/categories', categoryForm);
        toast.success('Category created');
      }
      setIsCategoryDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  // Article handlers
  const openArticleEditor = (article = null) => {
    if (article) {
      setArticleForm({
        title: article.title,
        content: article.content,
        category_id: article.category_id,
        excerpt: article.excerpt || '',
        tags: article.tags || [],
        is_published: article.is_published !== false
      });
      setSelectedArticle(article);
    } else {
      setArticleForm({
        title: '',
        content: '',
        category_id: selectedCategory?.id || '',
        excerpt: '',
        tags: [],
        is_published: true
      });
      setSelectedArticle(null);
    }
    setView('edit');
  };

  const saveArticle = async () => {
    if (!articleForm.title || !articleForm.content || !articleForm.category_id) {
      toast.error('Title, content, and category are required');
      return;
    }
    
    try {
      if (selectedArticle) {
        await api.put(`/admin/knowledge-base/articles/${selectedArticle.id}`, articleForm);
        toast.success('Article updated');
      } else {
        await api.post('/admin/knowledge-base/articles', articleForm);
        toast.success('Article created');
      }
      setView('list');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save article');
    }
  };

  const viewArticle = async (article) => {
    try {
      const res = await api.get(`/admin/knowledge-base/articles/${article.id}`);
      setSelectedArticle(res.data);
      setView('article');
    } catch (error) {
      toast.error('Failed to load article');
    }
  };

  // Delete handlers
  const confirmDelete = (type, item) => {
    setDeleteTarget({ type, item });
    setIsDeleteDialogOpen(true);
  };

  const executeDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      if (deleteTarget.type === 'category') {
        await api.delete(`/admin/knowledge-base/categories/${deleteTarget.item.id}`);
        toast.success('Category deleted');
      } else {
        await api.delete(`/admin/knowledge-base/articles/${deleteTarget.item.id}`);
        toast.success('Article deleted');
      }
      setIsDeleteDialogOpen(false);
      setDeleteTarget(null);
      if (view === 'article') setView('list');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  // Tag handlers
  const addTag = () => {
    if (tagInput && !articleForm.tags.includes(tagInput.toLowerCase())) {
      setArticleForm({ ...articleForm, tags: [...articleForm.tags, tagInput.toLowerCase()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setArticleForm({ ...articleForm, tags: articleForm.tags.filter(t => t !== tag) });
  };

  // Search
  const handleSearch = async () => {
    if (!searchTerm || searchTerm.length < 2) {
      loadData();
      return;
    }
    
    try {
      const res = await api.get(`/admin/knowledge-base/search?q=${encodeURIComponent(searchTerm)}`);
      setArticles(res.data.results || []);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const filteredArticles = selectedCategory 
    ? articles.filter(a => a.category_id === selectedCategory.id)
    : articles;

  // Render article view
  if (view === 'article' && selectedArticle) {
    return (
      <div className="p-6 space-y-6" data-testid="kb-article-view">
        <Button variant="ghost" onClick={() => setView('list')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Articles
        </Button>
        
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-2xl">{selectedArticle.title}</CardTitle>
                <CardDescription className="flex items-center gap-4 mt-2">
                  <span className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    {selectedArticle.views || 0} views
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {formatDate(selectedArticle.updated_at)}
                  </span>
                  <span className="flex items-center gap-1">
                    <History className="w-4 h-4" />
                    v{selectedArticle.version || 1}
                  </span>
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => openArticleEditor(selectedArticle)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button variant="destructive" onClick={() => confirmDelete('article', selectedArticle)}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {selectedArticle.tags?.length > 0 && (
              <div className="flex gap-2 mb-4">
                {selectedArticle.tags.map(tag => (
                  <Badge key={tag} variant="secondary">
                    <Tag className="w-3 h-3 mr-1" />
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
            
            <div className="prose max-w-none">
              <pre className="whitespace-pre-wrap font-sans text-base leading-relaxed">
                {selectedArticle.content}
              </pre>
            </div>
            
            {selectedArticle.versions?.length > 0 && (
              <div className="mt-8 border-t pt-4">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Version History
                </h4>
                <div className="space-y-2">
                  {selectedArticle.versions.map(v => (
                    <div key={v.id} className="flex items-center justify-between text-sm p-2 bg-muted rounded">
                      <span>Version {v.version} - {formatDate(v.created_at)}</span>
                      {v.restored_from && (
                        <Badge variant="outline">Restored from v{v.restored_from}</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render editor view
  if (view === 'edit') {
    return (
      <div className="p-6 space-y-6" data-testid="kb-article-editor">
        <Button variant="ghost" onClick={() => setView('list')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Cancel
        </Button>
        
        <Card>
          <CardHeader>
            <CardTitle>{selectedArticle ? 'Edit Article' : 'New Article'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={articleForm.title}
                onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
                placeholder="Article title"
                data-testid="article-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select
                value={articleForm.category_id}
                onValueChange={(value) => setArticleForm({ ...articleForm, category_id: value })}
              >
                <SelectTrigger data-testid="article-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Excerpt</Label>
              <Textarea
                value={articleForm.excerpt}
                onChange={(e) => setArticleForm({ ...articleForm, excerpt: e.target.value })}
                placeholder="Brief summary of the article"
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Content *</Label>
              <Textarea
                value={articleForm.content}
                onChange={(e) => setArticleForm({ ...articleForm, content: e.target.value })}
                placeholder="Article content (supports markdown)"
                rows={15}
                data-testid="article-content-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add a tag"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <Button type="button" variant="outline" onClick={addTag}>Add</Button>
              </div>
              {articleForm.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {articleForm.tags.map(tag => (
                    <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                      {tag} &times;
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={articleForm.is_published}
                onCheckedChange={(checked) => setArticleForm({ ...articleForm, is_published: checked })}
              />
              <Label>Published</Label>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button onClick={saveArticle} data-testid="save-article-btn">
                <Save className="w-4 h-4 mr-2" />
                Save Article
              </Button>
              <Button variant="outline" onClick={() => setView('list')}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render list view
  return (
    <div className="p-6 space-y-6" data-testid="kb-list-view">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BookOpen className="w-6 h-6" />
            Knowledge Base
          </h1>
          <p className="text-muted-foreground">Internal documentation and guides for admins</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => openCategoryDialog()}>
            <FolderOpen className="w-4 h-4 mr-2" />
            New Category
          </Button>
          <Button onClick={() => openArticleEditor()} data-testid="new-article-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Article
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10"
                data-testid="kb-search-input"
              />
            </div>
            <Button onClick={handleSearch}>Search</Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Categories Sidebar */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base">Categories</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[400px]">
              <div className="p-2 space-y-1">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`w-full flex items-center justify-between p-2 rounded-lg transition-colors ${
                    !selectedCategory ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <FolderOpen className="w-4 h-4" />
                    All Articles
                  </span>
                  <Badge variant={!selectedCategory ? 'secondary' : 'outline'}>
                    {articles.length}
                  </Badge>
                </button>
                
                {categories.map(cat => (
                  <div key={cat.id} className="group">
                    <button
                      onClick={() => setSelectedCategory(cat)}
                      className={`w-full flex items-center justify-between p-2 rounded-lg transition-colors ${
                        selectedCategory?.id === cat.id ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <FolderOpen className="w-4 h-4" />
                        {cat.name}
                      </span>
                      <Badge variant={selectedCategory?.id === cat.id ? 'secondary' : 'outline'}>
                        {cat.article_count || 0}
                      </Badge>
                    </button>
                    <div className="hidden group-hover:flex gap-1 px-2 pb-1">
                      <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => openCategoryDialog(cat)}>
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-destructive" onClick={() => confirmDelete('category', cat)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Articles List */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {selectedCategory ? selectedCategory.name : 'All Articles'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : filteredArticles.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No articles found</p>
                <Button className="mt-4" onClick={() => openArticleEditor()}>
                  Create First Article
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredArticles.map(article => (
                  <div 
                    key={article.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => viewArticle(article)}
                    data-testid={`article-item-${article.id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">{article.title}</h3>
                        {!article.is_published && (
                          <Badge variant="outline" className="text-yellow-600">Draft</Badge>
                        )}
                      </div>
                      {article.excerpt && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                          {article.excerpt}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(article.updated_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {article.views || 0}
                        </span>
                        {article.tags?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Tag className="w-3 h-3" />
                            {article.tags.length}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Category Dialog */}
      <Dialog open={isCategoryDialogOpen} onOpenChange={setIsCategoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedCategory ? 'Edit Category' : 'New Category'}</DialogTitle>
            <DialogDescription>
              Organize your knowledge base articles into categories
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="Category name"
                data-testid="category-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                placeholder="Optional description"
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Order</Label>
              <Input
                type="number"
                value={categoryForm.order}
                onChange={(e) => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCategoryDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveCategory} data-testid="save-category-btn">Save Category</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.type}?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.type === 'category' 
                ? 'This will permanently delete the category. Make sure it has no articles first.'
                : 'This will permanently delete this article and all its versions.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={executeDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminKnowledgeBase;
